import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { formatTimeToMinutes } from '@/helpers';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { previewAssignmentBookedMinutes } from '../../api';
import { PreviewAssignmentBookedMinutesRequest } from '../../types/dto';

const PREVIEW_QUERY_KEY = 'assignment-booked-minutes-preview';

const DEBOUNCE_MS = 300;

const TIME_PATTERN = /^\d{1,2}:\d{2}$/;

interface PreviewValues {
  userId: string;
  /** Committed (picker closed) dates, so picking a range doesn't fire a request per click */
  dateFrom: Date | null | undefined;
  dateTo: Date | null | undefined;
  scheduledWork: string;
  doSkipWeekdays: boolean;
  isBackgroundWork: boolean;
}

interface UseAssignmentBookedMinutesPreviewParams extends PreviewValues {
  open: boolean;
  assignmentId?: number;
  /** The values the modal opened with and their already known total, so opening it costs no request */
  initial?: { values: PreviewValues; bookedMinutes: number };
}

function buildPreviewKey(
  { userId, dateFrom, dateTo, scheduledWork, doSkipWeekdays, isBackgroundWork }: PreviewValues,
  assignmentId: number | undefined
): string | null {
  if (!userId || !dateFrom || !dateTo || !TIME_PATTERN.test(scheduledWork ?? '')) return null;

  const startDate = normalizeToStartOfDayUTC(dateFrom);
  const endDate = normalizeToStartOfDayUTC(dateTo);
  if (endDate < startDate) return null;

  const request: PreviewAssignmentBookedMinutesRequest = {
    userId: Number(userId),
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    minutesPerDay: formatTimeToMinutes(scheduledWork),
    doSkipWeekdays,
    isBackgroundWork,
    assignmentId,
  };

  return JSON.stringify(request);
}

/**
 * Live booked total for the assignment modal's values; `undefined` until they are complete.
 * Fetches only once the values differ from `initial`.
 */
export function useAssignmentBookedMinutesPreview({
  open,
  userId,
  dateFrom,
  dateTo,
  scheduledWork,
  doSkipWeekdays,
  isBackgroundWork,
  assignmentId,
  initial,
}: UseAssignmentBookedMinutesPreviewParams) {
  const requestKey = useMemo(
    () =>
      buildPreviewKey(
        { userId, dateFrom, dateTo, scheduledWork, doSkipWeekdays, isBackgroundWork },
        assignmentId
      ),
    [userId, dateFrom, dateTo, scheduledWork, doSkipWeekdays, isBackgroundWork, assignmentId]
  );
  const initialKey = initial ? buildPreviewKey(initial.values, assignmentId) : null;
  const isInitial = requestKey !== null && requestKey === initialKey;

  const debouncedKey = useDebouncedValue(requestKey, DEBOUNCE_MS);

  const query = useQuery({
    queryKey: [PREVIEW_QUERY_KEY, debouncedKey],
    queryFn: () =>
      previewAssignmentBookedMinutes(
        JSON.parse(debouncedKey ?? '{}') as PreviewAssignmentBookedMinutesRequest
      ),
    enabled: open && !!debouncedKey && debouncedKey !== initialKey,
    // Not cached: sibling assignments and time off can change it without touching this key
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    select: data => data.bookedMinutes,
  });

  const isActive = open && !!requestKey;

  return {
    bookedMinutes: isActive ? (isInitial ? initial?.bookedMinutes : query.data) : undefined,
    // Not for background refetches, which keep the current total on screen
    isLoading: isActive && !isInitial && (requestKey !== debouncedKey || query.isPending),
    isError: isActive && !isInitial && requestKey === debouncedKey && query.isError,
  };
}
